<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import type { TreeItemsStateType, TreeViewItemType } from '#components/treeView';
import TreeView from '#components/treeView/TreeView.vue';
import { useLanguage } from '#composables/useLanguage';
import { CUSTOMER_SEGMENTATION_TYPES, type CustomerSegmentationType } from '@carrefour-gcs/shared';
import { CSegmentedControl } from '@carrefour/design-system-components-vue3';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { computed, ref, type PropType } from 'vue';
import type { TreeView as LocalTreeView } from './composables/useProductData';

const { CONSOSTYLE, LIFESTAGE } = CUSTOMER_SEGMENTATION_TYPES;

const props = defineProps({
  consostyleItems: {
    type: Object as PropType<TreeViewItemType | LocalTreeView | null>,
    required: false,
    default: null
  },
  lifestageItems: {
    type: Object as PropType<TreeViewItemType | LocalTreeView | null>,
    required: false,
    default: null
  },
  defaultOpen: {
    type: Boolean,
    required: false,
    default: false
  },
  title: {
    type: String,
    required: true
  },
  testId: {
    type: String,
    required: false,
    default: undefined
  },
  consostyleLoading: {
    type: Boolean,
    required: false,
    default: false
  },
  lifestageLoading: {
    type: Boolean,
    required: false,
    default: false
  },
  itemsCount: {
    type: Number,
    required: false,
    default: 10
  }
});

const emit = defineEmits<{
  onConsostyleCheckedItemsChange: [state: TreeItemsStateType];
  onLifestageCheckedItemsChange: [state: TreeItemsStateType];
}>();

const { t } = useLanguage();

const selectedSegmentationType = ref<CustomerSegmentationType>(CONSOSTYLE);

const currentItems = computed(() =>
  selectedSegmentationType.value === CONSOSTYLE ? props.consostyleItems : props.lifestageItems
);

const isLoading = computed(() =>
  selectedSegmentationType.value === CONSOSTYLE ? props.consostyleLoading : props.lifestageLoading
);

const isItems = computed(
  () =>
    (props.consostyleItems &&
      Array.isArray(props.consostyleItems.children) &&
      props.consostyleItems.children.length > 0) ||
    (props.lifestageItems && Array.isArray(props.lifestageItems.children) && props.lifestageItems.children.length > 0)
);

const hasQueriesRun = computed(() => props.consostyleItems !== null || props.lifestageItems !== null);

const segmentationTypeOptions = computed(() => [
  {
    label: t('filterPopin.consostyleMicro'),
    value: CONSOSTYLE
  },
  {
    label: t('filterPopin.lifestageMicro'),
    value: LIFESTAGE
  }
]);

function handleOnCheckedItemsChange(checkedItems: TreeItemsStateType) {
  if (selectedSegmentationType.value === CONSOSTYLE) {
    emit('onConsostyleCheckedItemsChange', checkedItems);
  } else {
    emit('onLifestageCheckedItemsChange', checkedItems);
  }
}
</script>
<template>
  <Disclosure v-if="isItems || isLoading || hasQueriesRun" v-slot="{ open }" as="div" :default-open="defaultOpen">
    <DisclosureButton as="template">
      <ButtonBase
        variant="tertiary"
        size="medium"
        :icon-left="open ? 'chevron-up' : 'chevron-down'"
        :test-id="`disclosure-${testId}`"
      >
        <span class="opacity-50">{{ title }}</span>
      </ButtonBase>
    </DisclosureButton>

    <DisclosurePanel as="div">
      <div class="px-8 py-4 flex justify-center">
        <div class="w-fit">
          <CSegmentedControl v-model="selectedSegmentationType" :buttons="segmentationTypeOptions" />
        </div>
      </div>

      <TreeView
        :items="(currentItems as TreeViewItemType)?.children"
        :is-loading="isLoading"
        :loading-items-count="itemsCount"
        :loading-items-has-arrow="false"
        @on-checked-items-change="handleOnCheckedItemsChange"
      />
    </DisclosurePanel>
  </Disclosure>
</template>
